import React from 'react';
import PropTypes from 'prop-types';
import { getRequestDataFromRequest } from '../services/RequestContext';

import content from '../services/content';

import Search from './SearchPage';
import SearchV1 from './SearchPageV1';
import { verifyAndUpdateTaxonomyCookie } from '../utils/taxonomy/sharedUtils';

// PROJECT CAT NOTES
// Issues: Bootstrap styles in [...search].less used on old & new can't be split. Use module.less files
// Functionally, getInitialProps is a mess. It would be nice to split into two branches directly

const getContentTypes = function getContentTypes(facets = []) {
  facets = facets.filter(facet => facet.internalName === 'contentType');
  return facets.length > 0 ? facets[0].children : facets;
};

// Get the URL of the Root of the given facetUrl (e.g. 'grammar' returns 'ela'). This function is relatively fast since
// the allFacets array should only have the children that are rendered in the page so the DFS is quite efficient!
const getRootFacet = function getRootFacet(
  facetUrl = null,
  allFacets = [],
  facetType = 'subject'
) {
  if (!facetUrl || !allFacets || allFacets.length === 0) return null;

  // Filter facets to get the specific facetType
  let facets = allFacets.filter(facet => facet.internalName === facetType);
  facets = facets.length > 0 ? facets[0].children : facets;

  // Initialize the queue to be an array of arrays of form [(Root Facet), (Facet)]
  let queue = [];
  for (var fc of Object.keys(facets)) {
    queue.push([facets[fc], facets[fc]]);
  }
  // Run DFS keeping track of the RootURL of each facet. If we find the facet with urlName=facetUrl, return the Root.
  let first = null;
  while (queue.length > 0) {
    first = queue.shift();
    if (first[1]?.['urlName'] === facetUrl) return first[0];
    if (first[1]?.['children']) {
      first[1]['children'].forEach(child => queue.push([first[0], child]));
    }
  }
  return null;
};

const SearchPageWrapper = result => {
  // Destructure initialprops to pass to search components
  let {
    total,
    pages,
    urlPath,
    meta,
    filter,
    currentFilters,
    facets,
    results,
    suggestions,
    replaceUrl,
    error,
    selectedCategory,
    viableCategories,
    isNewTaxonomy,
    params,
    selectedFilters,
    viableGrades,
    viableFilters,
    viableContentTypes,
    hasSubjectFilters,
  } = result;

  return (
    <div>
      {isNewTaxonomy && (
        <Search
          total={total}
          pages={pages}
          urlPath={urlPath}
          meta={meta}
          filter={filter}
          currentFilters={currentFilters}
          facets={facets}
          results={results}
          suggestions={suggestions}
          replaceUrl={replaceUrl}
          error={error}
          viableCategories={viableCategories}
          selectedCategory={selectedCategory}
          params={params}
          selectedFilters={selectedFilters}
          viableGrades={viableGrades}
          viableFilters={viableFilters}
          viableContentTypes={viableContentTypes}
          hasSubjectFilters={hasSubjectFilters}
        />
      )}
      {!isNewTaxonomy && (
        <SearchV1
          total={total}
          pages={pages}
          urlPath={urlPath}
          meta={meta}
          filter={filter}
          currentFilters={currentFilters}
          facets={facets}
          results={results}
          suggestions={suggestions}
          replaceUrl={replaceUrl}
          error={error}
        />
      )}
    </div>
  );
};

// Initial props are not passed into components (so can't call getInitial Props from there)
SearchPageWrapper.getInitialProps = async ({ asPath, req, res }) => {
  const requestData = getRequestDataFromRequest(req);
  const isNewTaxonomy = requestData.useNewTaxonomy;

  // Check if we should update the cookie value
  verifyAndUpdateTaxonomyCookie(asPath, res, isNewTaxonomy);
  let result = {};

  // the change bellow is hardcode for testing purpose, and would be reverted before merge into master
  let queryString = asPath
    // Just the querystring part
    .replace(/^[^?]+[?]?/, '');

  const urlParams = new URLSearchParams(queryString);
  const queryTerm = urlParams.get('q'); // Extract the 'q' parameter

  // EDENG-3488, We don't want to index any of the following:
  // Anything that contains non-English and non-Spanish characters. Specifically, Russian, Chinese, likely others.
  // Anything that contains phone numbers.
  // Anything that contains a URL.
  const shouldNoIndex = query => {
    if (!query) return false; // Ignore empty queries

    // Match non-English and non-Spanish characters
    const nonEnglishOrSpanishRegex = /[^\x20-\x7E\u00C0-\u024F]/; // Latin characters + accents
    if (nonEnglishOrSpanishRegex.test(query)) {
      return true;
    }
    // Match phone numbers (e.g., formats like 123-456-7890 or (123) 456-7890)
    const phoneRegex = /(\+?\d{1,3}[-.\s]?)?(\(?\d{3}\)?[-.\s]?)?\d{3}[-.\s]?\d{4}/;
    if (phoneRegex.test(query)) {
      return true;
    }
    // Match URLs (e.g., http://example.com, www.example.com, example.com/path)
    const urlRegex = /(https?:\/\/|www\.)[^\s]+/;
    if (urlRegex.test(query)) {
      return true;
    }
    return false;
  };
  
  const noIndex = shouldNoIndex(queryTerm);

  if (!isNewTaxonomy) {
    queryString = asPath
      // Just the querystring part
      .replace(/^[^?]+[?]?/, '')
      // Split into k=v parts
      .split('&')
      // Only include known search params
      .filter(val => val.match(/^(page|sort|q|grades)=/))
      .join('&');
  }

  // The url to search with (pathName + known query params)
  let searchUrl = asPath.replace(/\?.*$/, '');
  if (queryString.length) {
    searchUrl += '?' + decodeURIComponent(queryString);
  }

  try {
    if (req !== undefined) {
      result = isNewTaxonomy
        ? await content(req).search(searchUrl, true)
        : await content(req).searchV1(searchUrl, true);

      // If rendering on server-side, only allow valid urls and perform an actual redirect
      if ('redirect' in result) {
        let redirectUrl = result.redirect;
        // Persist some tracking querystring params when redirecting
        const additionalQuerystringParams = [];
        Object.keys(req.query).forEach(key => {
          if (key.match(/^(cid$|utm_|gclid$|msclkid$|fbclid$)/) && req.query[key]) {
            additionalQuerystringParams.push(key + '=' + req.query[key]);
          }
        });
        if (additionalQuerystringParams.length) {
          redirectUrl +=
            (redirectUrl.match(/\?/) ? '&' : '?') +
            additionalQuerystringParams.join('&');
        }

        return res.redirect(301, redirectUrl);
      }
    } else {
      result = isNewTaxonomy
        ? await content(req).search(searchUrl)
        : await content(req).searchV1(searchUrl);

      // If rendering on client-side, update the url if the urlPath is different from the current path
      if (result.redirect) {
        result = Object.assign({}, result, { replaceUrl: true });
      }
    }
  } catch (e) {
    console.error(e);
    result = {
      urlPath: searchUrl,
      meta: {
        title: 'Search Our Content Library',
        metaTitle: 'Learning Library',
      },
      error: true,
    };
  }

  result.meta = {
    ...result.meta,
    index: !noIndex, // Pass the index property dynamically
  };

  result.navProps = {
    page: 'search',
    searchMetaTitle: result?.meta?.title,
    query: result?.filter?.q,
    urlPath: result.urlPath,
  };

  // We need the 'nesting' name of the contentType to match the other parts of the site for GA & GTM.
  let contentTypeNesting = null;
  if (result?.filter?.facets?.contentType) {
    contentTypeNesting = getContentTypes(result?.facets).filter(
      facet => facet.urlName === result.filter.facets.contentType
    );
    contentTypeNesting = contentTypeNesting?.[0]?.['nesting'];
  }

  // Footer props for Tracking
  result.footerProps = {
    grades: result?.filter?.facets?.grades ? result.filter.facets.grades : [],
    subjects: [
      getRootFacet(result?.filter?.facets?.subject, result?.facets, 'subject')
        ?.urlName,
    ],
    contentType: contentTypeNesting,
    pageType: 'Search',
  };

  result.hasSubjectFilters = false;
  // Display the top-level categories as "Subjects" filter
  if (isNewTaxonomy && result.viableSubjectCategories) {
    result.hasSubjectFilters = true;
    let selectedSubjectUrlSlice = null;
    if (result.selectedCategory && result.selectedCategory.text !== 'root') {
      // Get selected level 1 category (subject)
      selectedSubjectUrlSlice = result.viableCategories.children.urlSlice;
    }
    const subjectFilter = convertSubjectCategoriesToFilter(
      result.viableSubjectCategories,
      selectedSubjectUrlSlice
    );

    result.viableFilters.push(subjectFilter);

    // Add to selected filters only if not at root category
    if (selectedSubjectUrlSlice) {
      const selectedSubjectFilter = {
        name: 'Subjects',
        attributes: subjectFilter.attributes.map(attribute => ({
          name: attribute.name,
          url: attribute.href,
        })),
        selectedAttribute: subjectFilter.attributes.find(
          attribute => attribute.selected
        ),
      };
      result.selectedFilters.unshift(selectedSubjectFilter);
    }
  }

  result.isNewTaxonomy = isNewTaxonomy; // Append newTaxonomy to return object
  return result;
};

function convertSubjectCategoriesToFilter(
  viableSubjectCategories,
  selectedSubjectUrlSlice
) {
  let attributes = [];
  let anyContent = false;

  viableSubjectCategories.forEach(subjectCategory => {
    if (subjectCategory.count > 0) {
      anyContent = true;
    }

    attributes.push({
      name: subjectCategory.text,
      urlSlice: subjectCategory.urlSlice,
      count: subjectCategory.count,
      selected: selectedSubjectUrlSlice === subjectCategory.urlSlice,
      href: subjectCategory.href,
    });
  });

  return {
    name: 'Subjects',
    anyContent: anyContent,
    filterRank: -1,
    attributes: attributes,
  };
}

const contextualLinkPropTypes = {
  url: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
};

const metaPropTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  metaTitle: PropTypes.string.isRequired,
  metaDescription: PropTypes.string,
  bottomTitle: PropTypes.string,
  bottomDescription: PropTypes.string,
  contextualLinksTitle: PropTypes.string,
  contextualLinks: PropTypes.arrayOf(PropTypes.shape(contextualLinkPropTypes)),
  index: PropTypes.bool,
};

const filterPropTypes = {
  q: PropTypes.string,
  page: PropTypes.number,
  sort: PropTypes.string,
  facets: PropTypes.shape({
    grades: PropTypes.arrayOf(PropTypes.string),
    contentType: PropTypes.string,
    subject: PropTypes.string,
    topic: PropTypes.string,
    standard: PropTypes.string,
    el: PropTypes.string,
    interactive: PropTypes.string,
  }),
};

const facetChildrenPropTypes = {
  urlName: PropTypes.string.isRequired,
  nesting: PropTypes.string.isRequired,
  display: PropTypes.string.isRequired,
  disabled: PropTypes.bool.isRequired,
  selected: PropTypes.bool.isRequired,
  count: PropTypes.number.isRequired,
};

const facetPropTypes = {
  internalName: PropTypes.string.isRequired,
  display: PropTypes.string.isRequired,
  children: PropTypes.arrayOf(
    PropTypes.shape(facetChildrenPropTypes).isRequired
  ),
};

const resultPropTypes = {
  type: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  url: PropTypes.string.isRequired,
  thumbnail: PropTypes.object,
  category: PropTypes.string,
  subject: PropTypes.string,
  grade: PropTypes.string.isRequired,
  rating: PropTypes.number,
  new: PropTypes.bool,
  answersheet: PropTypes.bool.isRequired,
  premium: PropTypes.bool,
  el: PropTypes.bool.isRequired,
  id: PropTypes.string.isRequired,
  meta: PropTypes.object,
  isInteractive: PropTypes.bool,
};

const currentFiltersPropTypes = {
  display: PropTypes.string,
  group: PropTypes.string,
  href: PropTypes.string,
};

const TaxonomyFilterAttributePropTypes = {
  name: PropTypes.string,
  url: PropTypes.string,
};

const TaxonomyFilterPropTypes = {
  name: PropTypes.string,
  url: PropTypes.string,
  attributes: PropTypes.arrayOf(
    PropTypes.shape(TaxonomyFilterAttributePropTypes)
  ),
};

const GradeFilterAttributePropTypes = {
  name: PropTypes.string,
  urlSlice: PropTypes.string,
  count: PropTypes.number,
  selected: PropTypes.bool,
  href: PropTypes.string,
  shortName: PropTypes.string,
  id: PropTypes.number,
};

const GradeFilterPropTypes = {
  name: PropTypes.string,
  href: PropTypes.string,
  anyContent: PropTypes.bool,
  attributes: PropTypes.arrayOf(PropTypes.shape(GradeFilterAttributePropTypes)),
  filterRank: PropTypes.number,
};

const ViableAttributePropTypes = {
  name: PropTypes.string,
  urlSlice: PropTypes.string,
  count: PropTypes.number,
  selected: PropTypes.bool,
  href: PropTypes.string,
};

const ViableFilterPropTypes = {
  name: PropTypes.string,
  urlSlice: PropTypes.string,
  anyContent: PropTypes.bool,
  attributes: PropTypes.arrayOf(PropTypes.shape(ViableAttributePropTypes)),
  filterRank: PropTypes.number,
};

const taxonomyCategoryPropTypes = {
  children: PropTypes.arrayOf(PropTypes.shape(this)),
  urlSlice: PropTypes.string.isRequired,
  text: PropTypes.string.isRequired,
  visible: PropTypes.bool.isRequired,
  shortCategoryName: PropTypes.string.isRequired,
  path: PropTypes.string.isRequired,
};

const viableCategoriesPropTypes = {
  urlSlice: PropTypes.string.isRequired,
  count: PropTypes.number.isRequired,
  text: PropTypes.string.isRequired,
  href: PropTypes.string.isRequired,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.shape(this)),
    PropTypes.shape(this),
  ]),
};

const paramsPropTypes = {
  page: PropTypes.number,
  sort: PropTypes.string,
  q: PropTypes.string,
};

SearchPageWrapper.propTypes = {
  total: PropTypes.number,
  pages: PropTypes.number,
  urlPath: PropTypes.string.isRequired,
  meta: PropTypes.shape(metaPropTypes).isRequired,
  params: PropTypes.shape(paramsPropTypes),
  filter: PropTypes.shape(filterPropTypes),
  currentFilters: PropTypes.arrayOf(PropTypes.shape(currentFiltersPropTypes)),
  facets: PropTypes.arrayOf(PropTypes.shape(facetPropTypes)),
  results: PropTypes.arrayOf(PropTypes.shape(resultPropTypes)),
  suggestions: PropTypes.arrayOf(PropTypes.string),
  replaceUrl: PropTypes.bool,
  error: PropTypes.bool,
  isNewTaxonomy: PropTypes.bool,
  selectedCategory: PropTypes.shape(taxonomyCategoryPropTypes),
  viableCategories: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.shape(viableCategoriesPropTypes)),
    PropTypes.shape(viableCategoriesPropTypes),
  ]),
  selectedFilters: PropTypes.arrayOf(PropTypes.shape(TaxonomyFilterPropTypes)),
  viableGrades: PropTypes.shape(GradeFilterPropTypes),
  viableFilters: PropTypes.arrayOf(PropTypes.shape(ViableFilterPropTypes)),
  viableContentTypes: PropTypes.shape(ViableFilterPropTypes),
  hasSubjectFilters: PropTypes.bool,
};

export default SearchPageWrapper;
